import axios, { endpoints } from '../lib/axios';

import type { PaginatorResponse } from '../types/common';
import type {
  MediaModel,
  LinkTypeEnum,
  MediaTypeEnum,
  RedactionFieldEnum,
  MediaEditorStatusEnum,
} from '../types/media';

interface GetMediasParams {
  id?: number | null;
  name?: string;
  url?: string;
  searchTerm?: string;
  email?: string;
  withoutEmail?: boolean;
  linkType?: LinkTypeEnum | null;
  mediaType?: MediaTypeEnum | null;
  categories?: number[];
  country?: string[];
  language?: string;
  redactionField?: RedactionFieldEnum | null;
  active?: boolean | null;
  editorStatus?: MediaEditorStatusEnum | null;
  mediaId?: number;
  drMin?: number | null;
  drMax?: number | null;
  daMin?: number | null;
  daMax?: number | null;
  tfMin?: number | null;
  tfMax?: number | null;
  trafficMin?: number | null;
  trafficMax?: number | null;
  priceMin?: number | null;
  priceMax?: number | null;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  page?: number | null;
  pageSize?: number | null;
}

export interface ImportMediaResponse {
  success: boolean;
  importedCount: number;
  updatedCount: number;
  errorsCount: number;
  message: string;
}

export async function getMedias(filters: GetMediasParams): Promise<PaginatorResponse<MediaModel>> {
  try {
    const response = await axios.post(endpoints.medias.getAll, filters);
    return response.data;
  } catch (error) {
    console.error('API error fetching medias:', error);
    throw error;
  }
}

export async function getMedia(id: number): Promise<MediaModel> {
  try {
    const response = await axios.get(endpoints.medias.getOne(id));
    return response.data;
  } catch (error) {
    console.error(`API error fetching media ${id}:`, error);
    throw error;
  }
}

// Toggle inmediato de "publicación rápida" (endpoint ligero dedicado, no reenvía todo el medio).
export async function setMediaFastPublication(id: number, enabled: boolean): Promise<void> {
  await axios.post(endpoints.medias.fastPublication(id), { enabled });
}

export async function updateMedia(id: number, mediaData: Partial<MediaModel>): Promise<MediaModel> {
  try {
    const response = await axios.put(endpoints.medias.update(id), mediaData);
    return response.data;
  } catch (error) {
    console.error(`API error updating media ${id}:`, error);
    throw error;
  }
}

export async function importMedias(file: File): Promise<ImportMediaResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(endpoints.medias.import, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('API error importing medias:', error);
    throw error;
  }
}

export async function approveMediaEditor(id: number): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.post(endpoints.medias.approveEditor(id));
    return response.data;
  } catch (error) {
    console.error(`API error approving media editor ${id}:`, error);
    throw error;
  }
}

export async function rejectMediaEditor(id: number): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.post(endpoints.medias.rejectEditor(id));
    return response.data;
  } catch (error) {
    console.error(`API error rejecting media editor ${id}:`, error);
    throw error;
  }
}

export interface BulkMediaEditorActionResult {
  mediaId: number;
  mediaName: string;
  mediaUrl: string;
  success: boolean;
  errorMessage?: string;
}

export interface BulkMediaEditorActionResponse {
  totalProcessed: number;
  successCount: number;
  errorCount: number;
  results: BulkMediaEditorActionResult[];
}

export async function bulkApproveMediaEditor(
  mediaIds: number[]
): Promise<{ success: boolean; message: string; data: BulkMediaEditorActionResponse }> {
  try {
    const response = await axios.post(endpoints.medias.bulkApprove, { mediaIds });
    return response.data;
  } catch (error) {
    console.error('API error bulk approving medias:', error);
    throw error;
  }
}

export async function bulkRejectMediaEditor(
  mediaIds: number[]
): Promise<{ success: boolean; message: string; data: BulkMediaEditorActionResponse }> {
  try {
    const response = await axios.post(endpoints.medias.bulkReject, { mediaIds });
    return response.data;
  } catch (error) {
    console.error('API error bulk rejecting medias:', error);
    throw error;
  }
}

export interface BulkMediaFilterActionRequest {
  name?: string;
  url?: string;
  email?: string;
  searchTerm?: string;
  mediaId?: number;
  categories?: number[];
  mediaType?: MediaTypeEnum | null;
  linkType?: LinkTypeEnum | null;
  country?: string[];
  language?: string;
  redactionField?: RedactionFieldEnum | null;
  active?: boolean | null;
  drMin?: number | null;
  drMax?: number | null;
  daMin?: number | null;
  daMax?: number | null;
  tfMin?: number | null;
  tfMax?: number | null;
  trafficMin?: number | null;
  trafficMax?: number | null;
  priceMin?: number | null;
  priceMax?: number | null;
}

export async function bulkApproveByFilter(
  filters: BulkMediaFilterActionRequest
): Promise<{ success: boolean; message: string; data: BulkMediaEditorActionResponse }> {
  try {
    const response = await axios.post(endpoints.medias.bulkApproveByFilter, filters);
    return response.data;
  } catch (error) {
    console.error('API error bulk approving medias by filter:', error);
    throw error;
  }
}

export async function bulkRejectByFilter(
  filters: BulkMediaFilterActionRequest
): Promise<{ success: boolean; message: string; data: BulkMediaEditorActionResponse }> {
  try {
    const response = await axios.post(endpoints.medias.bulkRejectByFilter, filters);
    return response.data;
  } catch (error) {
    console.error('API error bulk rejecting medias by filter:', error);
    throw error;
  }
}

export async function queueScreenshot(id: number): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.post(endpoints.medias.queueScreenshot(id));
    return response.data;
  } catch (error) {
    console.error(`API error queueing screenshot for media ${id}:`, error);
    throw error;
  }
}

export async function queueDomainAge(id: number): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.post(endpoints.medias.queueDomainAge(id));
    return response.data;
  } catch (error) {
    console.error(`API error queueing domain age for media ${id}:`, error);
    throw error;
  }
}

export async function addEditorKeyword(
  mediaId: number,
  keyword: string
): Promise<{ success: boolean; message: string; data: { id: number; keyword: string; createdAt: Date } }> {
  try {
    const response = await axios.post(endpoints.medias.addKeyword(mediaId), { keyword });
    return response.data;
  } catch (error) {
    console.error(`API error adding keyword to media ${mediaId}:`, error);
    throw error;
  }
}

export async function deleteEditorKeyword(
  mediaId: number,
  keywordId: number
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.delete(endpoints.medias.deleteKeyword(mediaId, keywordId));
    return response.data;
  } catch (error) {
    console.error(`API error deleting keyword ${keywordId} from media ${mediaId}:`, error);
    throw error;
  }
}
